export interface StructuredAddress {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
}

export const COUNTRY_OPTIONS = [
  'Australia',
  'China',
  'United States',
  'New Zealand',
  'United Kingdom',
  'India',
  'Vietnam',
  'Bangladesh',
  'Indonesia',
  'Thailand',
  'South Korea',
  'Japan',
  'Taiwan',
  'Hong Kong',
  'Singapore',
  'Malaysia',
  'Philippines',
  'Pakistan',
  'Sri Lanka',
  'Cambodia',
  'Turkey',
  'Italy',
  'Portugal',
  'Germany',
  'Mexico',
  'Canada',
] as const;

/**
 * Parse the `address` field from the DB.
 * If it was stored as a JSON StructuredAddress, return the parsed object.
 * Otherwise return a StructuredAddress with the raw string in the `street` field.
 */
export function parseAddress(raw: string | null | undefined): StructuredAddress {
  if (!raw) return { street: '', suburb: '', state: '', postcode: '', country: 'Australia' };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'street' in parsed) {
      return parsed as StructuredAddress;
    }
  } catch {
    // Not JSON - legacy flat string
  }
  return { street: raw, suburb: '', state: '', postcode: '', country: '' };
}

/**
 * Serialize a StructuredAddress to a JSON string for storage in the DB `address` field.
 */
export function serializeAddress(addr: StructuredAddress): string {
  // If all fields are empty, return empty string
  const hasContent = addr.street || addr.suburb || addr.state || addr.postcode || addr.country;
  if (!hasContent) return '';
  return JSON.stringify(addr);
}

/**
 * Format a structured address for display (single line).
 */
export function formatAddress(addr: StructuredAddress): string {
  const parts = [addr.street, addr.suburb, addr.state, addr.postcode, addr.country].filter(Boolean);
  return parts.join(', ');
}

/**
 * Extract the country from an address string (structured JSON or raw).
 */
export function extractCountry(raw: string | null | undefined): string {
  const addr = parseAddress(raw);
  return addr.country || '';
}

/**
 * Parse comma-separated email string into an array.
 */
export function parseEmails(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(',').map((e) => e.trim()).filter(Boolean);
}

/**
 * Serialize email array to comma-separated string for DB storage.
 */
export function serializeEmails(emails: string[]): string {
  return emails.filter(Boolean).join(', ');
}

export interface Supplier {
  id: string;
  organization_id?: string;
  title: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  minimum_order_quantity: string;
  products: string[];
  default_currency: string | null;
  payment_terms_days: number | null;
  deposit_percent: number | null;
  xero_contact_id: string | null;
  default_tax_rate_id: string | null;
  default_account_code: string | null;
  hide_prices_on_pdf: boolean;
  lead_time_standard_days?: number | null;
  lead_time_sample_days?: number | null;
  lead_time_urgent_days?: number | null;
  order_count: number;
  total_spent: number;
  /** The PrintRun catalog supplier this supplier is linked to (read-only; set by Link or Add to my suppliers). */
  catalogSupplierId?: string | null;
  /**
   * BLU-1506 "When to buy" (read-only here; written by `suppliers.setSupply`,
   * admin only): `order_and_wait` buys blanks before batching and orders wait,
   * `buy_on_print_day` buys them the morning of the print. Absent on an older
   * backend, which reads as Buy on print day.
   */
  supplyMode?: string;
  /** `daily` (a floor-local send time, display only) or `when_pressed`. */
  sendMode?: string;
  /** HH:MM, floor-local; null when the supplier sends only when pressed. */
  sendTime?: string | null;
  /** When the supplier last switched to Order and wait. */
  supplyModeSince?: string | null;
  /**
   * BLU-1526 "Follow supplier stock" (written by `suppliers.setFollowStock`,
   * admin only): just-in-time blanks of the linked catalog close in the store
   * while the supplier has fewer than this many. null or absent = off.
   */
  followStockBelow?: number | null;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CreateSupplierData {
  title: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  description?: string;
  minimum_order_quantity?: string;
  products?: string[];
  default_currency?: string | null;
  payment_terms_days?: number | null;
  deposit_percent?: number | null;
  xero_contact_id?: string | null;
  default_tax_rate_id?: string | null;
  default_account_code?: string | null;
  hide_prices_on_pdf?: boolean;
  lead_time_standard_days?: number | null;
  lead_time_sample_days?: number | null;
  lead_time_urgent_days?: number | null;
  is_active?: boolean;
}
